import { useCallback, useState } from 'react'

import { externalAuthApi } from '../api'
import { ERROR_MESSAGES } from '../utils/errorMessages'
import { useAuthBase } from './useAuthBase'

export interface UseEntityNoticeSignupOtpOptions {
  email: string
  onVerified: (token: string) => void
}

interface UseEntityNoticeSignupOtpReturn {
  isLoading: boolean
  error: string | undefined
  setError: (error: string | undefined) => void
  validateCode: (code: string) => Promise<void>
  resendCode: () => Promise<boolean>
}

/**
 * Drives the OTP confirmation step of the entity-notice fake-door signup
 * (cells A/B), in-modal. Unlike `useSignupOtp`, a valid code never navigates
 * anywhere — R2 forbids leaving the iframe mid-flow — it hands the signup
 * token to `onVerified` so the caller can advance the in-memory step machine
 * to "Complete seus dados".
 */
export function useEntityNoticeSignupOtp(
  options: UseEntityNoticeSignupOtpOptions
): UseEntityNoticeSignupOtpReturn {
  const { email, onVerified } = options
  const { error, setError, handleAsyncOperation } = useAuthBase()
  const [isLoading, setIsLoading] = useState(false)

  const validateCode = useCallback(
    async (code: string) => {
      setIsLoading(true)
      setError(undefined)
      const result = await handleAsyncOperation(async () =>
        externalAuthApi.validateSignupOtp({ email, otp: code })
      )

      if (!result) {
        setIsLoading(false)
        return
      }

      const { response, data } = result

      if (response.ok && data?.token) {
        setIsLoading(false)
        onVerified(data.token)
        return
      }

      setError(data?.message || ERROR_MESSAGES.signup.otp.server.invalid)
      setIsLoading(false)
    },
    [email, handleAsyncOperation, onVerified, setError]
  )

  const resendCode = useCallback(async (): Promise<boolean> => {
    const result = await handleAsyncOperation(async () => externalAuthApi.sendSignupOtp({ email }))
    return !!result?.response.ok
  }, [email, handleAsyncOperation])

  return {
    isLoading,
    error,
    setError,
    validateCode,
    resendCode,
  }
}
